import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, Button, IconButton, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import AccountCard, { MockupStateLinks, accountBodySx, accountLinkSx } from '../components/AccountCard';
import { BLUE_TEXT, INTER, SOFT_BLUE, primaryButtonSx } from '../publicStyles';

const BASE = '/mockup/public/signup';
const INVITED = `${BASE}?email=maya.chen%40example.edu&invite=preview`;
const CLASS_CODE = `${BASE}?c=CHIN3021`;
const CLASS_CODE_INVALID = `${CLASS_CODE}&state=class-code-invalid`;
const EXISTS = `${BASE}?state=exists`;
const INVITE_INVALID = `${INVITED}&state=invite-invalid`;
const DONE = `${BASE}?state=done&email=maya.chen%40example.edu`;

// Proposal for /registration. Self-registration creates a student account. Class invitations
// (student or co-teacher) link here with ?email=…&invite=…, and course access codes use ?c=….
// Registering does not sign the person in: the real page asks them to verify their email and log in.
export default function PublicSignupPage() {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const state = params.get('state');
    const invitedEmail = params.get('invite') ? params.get('email') ?? '' : '';
    const classCode = params.get('c');
    const current = `${location.pathname}${location.search}`;
    const controls = <MockupStateLinks current={current} options={[
        { label: 'Default', to: BASE },
        { label: 'From an invitation', to: INVITED },
        { label: 'With a class code', to: CLASS_CODE },
        { label: 'Class code not valid', to: CLASS_CODE_INVALID },
        { label: 'Email already used', to: EXISTS },
        { label: 'Invitation not valid', to: INVITE_INVALID },
        { label: 'Account created', to: DONE },
    ]} />;

    const [email, setEmail] = React.useState(invitedEmail || (state === 'exists' ? 'maya.chen@example.edu' : ''));
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [mismatch, setMismatch] = React.useState(false);
    const confirmRef = React.useRef<HTMLInputElement>(null);
    const errorRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (state === 'exists' || state === 'invite-invalid') errorRef.current?.focus();
    }, [state]);

    if (state === 'done') {
        return (
            <AccountCard title="Check your email" controls={controls}>
                <Alert severity="success" role="status" sx={{ mt: 2, borderRadius: '12px' }}>
                    Account preview for {params.get('email')}. No account was created and no email was sent.
                </Alert>
                <Typography sx={{ ...accountBodySx, mt: 2 }}>
                    We sent a link to confirm your email address. It can take up to 30 minutes, so check your spam folder too.
                </Typography>
                <Typography sx={{ ...accountBodySx, mt: 1.5 }}>
                    You don&apos;t have to wait: you can log in now and confirm your email later.
                </Typography>
                <Button component={RouterLink} to="/mockup/public/login" sx={{ ...primaryButtonSx, minHeight: 48, mt: 3, width: '100%' }}>Log in</Button>
            </AccountCard>
        );
    }

    const invalidLink = state === 'invite-invalid' || state === 'class-code-invalid';
    const duplicateEmail = state === 'exists' && email.trim().toLowerCase() === 'maya.chen@example.edu';
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (invalidLink || duplicateEmail) return;
        if (password !== confirmPassword) {
            setMismatch(true);
            confirmRef.current?.focus();
            return;
        }
        history.push(`${BASE}?state=done&email=${encodeURIComponent(email)}`);
    };

    const errorMessage = duplicateEmail
        ? <>An account already uses this email. <Link component={RouterLink} to="/mockup/public/login" sx={{ fontWeight: 700, color: 'inherit' }}>Log in</Link> or <Link component={RouterLink} to="/mockup/public/forgot-password" sx={{ fontWeight: 700, color: 'inherit' }}>reset your password</Link>.</>
        : state === 'invite-invalid'
            ? <>This invitation link isn&apos;t valid. Ask the person who invited you to send it again, or log in if you already set up your account.</>
            : state === 'class-code-invalid'
                ? <>This class code isn&apos;t valid. Ask your teacher for a new signup link, or <Link component={RouterLink} to={BASE} sx={{ fontWeight: 700, color: 'inherit' }}>create an account without joining a class</Link>.</>
            : null;

    const passwordAdornment = (
        <InputAdornment position="end">
            <IconButton onClick={() => setShowPassword(value => !value)} edge="end"
                aria-label={showPassword ? 'Hide passwords' : 'Show passwords'} aria-pressed={showPassword}>
                {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <AccountCard width={480} controls={controls}
            title={invitedEmail ? 'Set up your account' : 'Create a student account'}
            intro={invitedEmail
                ? 'You’ve been invited to a class on VerbaSchola. Choose a password to get started.'
                : classCode
                    ? 'Create your account to join the class.'
                    : 'If you got an invitation email, use its link instead so you join the right class.'}
            footer={invitedEmail ? undefined : <>Teachers: <Link component={RouterLink} to="/mockup/public/pilot" underline="hover" sx={{ ...accountLinkSx, fontSize: 13 }}>request a pilot</Link> to get a teacher account.</>}>
            {classCode && !errorMessage && (
                <Box sx={{ mt: 2.5, px: 2, py: 1.5, borderRadius: '12px', backgroundColor: SOFT_BLUE }}>
                    <Typography sx={{ fontFamily: INTER, fontSize: 14, color: BLUE_TEXT }}>
                        Joining with class code <Box component="strong" sx={{ letterSpacing: '0.04em' }}>{classCode}</Box>
                    </Typography>
                </Box>
            )}
            {errorMessage && (
                <Alert severity="error" ref={errorRef} tabIndex={-1} id="signup-error" sx={{ mt: 2.5, borderRadius: '12px', outline: 'none' }}>
                    {errorMessage}
                </Alert>
            )}
            <Stack component="form" spacing={2} onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="First name" name="firstName" autoComplete="given-name" required fullWidth />
                    <TextField label="Last name" name="lastName" autoComplete="family-name" required fullWidth />
                </Stack>
                <TextField label="Email address" name="email" type="email" autoComplete="email" required fullWidth
                    value={email} onChange={event => setEmail(event.target.value)}
                    error={duplicateEmail}
                    InputProps={{ readOnly: Boolean(invitedEmail) }}
                    inputProps={{ 'aria-describedby': duplicateEmail ? 'signup-error' : undefined }}
                    helperText={invitedEmail ? 'This is the address you were invited with.' : undefined} />
                <TextField label="New password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required fullWidth
                    value={password} onChange={event => { setPassword(event.target.value); setMismatch(false); }}
                    InputProps={{ endAdornment: passwordAdornment }} />
                <TextField label="Confirm new password" name="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required fullWidth
                    value={confirmPassword} onChange={event => { setConfirmPassword(event.target.value); setMismatch(false); }}
                    error={mismatch} helperText={mismatch ? 'The passwords don’t match. Enter the same password twice.' : undefined} inputRef={confirmRef} />
                <Button type="submit" disabled={invalidLink || duplicateEmail} sx={{ ...primaryButtonSx, minHeight: 48 }}>{invitedEmail ? 'Set up account' : 'Create account'}</Button>
            </Stack>
            <Typography sx={{ fontFamily: INTER, fontSize: 14, color: accountBodySx.color, textAlign: 'center', mt: 3 }}>
                Already have an account?{' '}
                <Link component={RouterLink} to="/mockup/public/login" underline="hover" sx={accountLinkSx}>Log in</Link>
            </Typography>
        </AccountCard>
    );
}
